/*
================================================================================
FILE HEADER — apps/studio-web/src/components/TestLibrary.tsx
================================================================================

CHANGE LOG
----------
| #  | Date & Time      | Who               | Why                     | What Changed                             |
|----|------------------|-------------------|-------------------------|------------------------------------------|
| 1  | 2026-06-25 00:00 | Claude Sonnet 4.6 | Test Library (Phase 3)  | Created — Mimiq-style table, suite tree  |
| 2  | 2026-06-25 00:00 | Claude Sonnet 4.6 | Execution flow          | Added Run Selected + onRunSelected prop  |
| 3  | 2026-07-02 00:00 | Claude Sonnet 4.6 | Mimiq Test Cases parity | + Tag / - Tag popovers (bulk add/remove  |
|    |                  |                   |                         | tags), Move Selected modal (change suite)|
|    |                  |                   |                         | tag filter chips row, date filters,      |
|    |                  |                   |                         | Export Selected, N tests count,          |
|    |                  |                   |                         | inline "add tag" on selected rows        |

FILES THAT CALL / IMPORT THIS FILE
------------------------------------
- apps/studio-web/src/App.tsx

FUNCTIONS / EXPORTS COVERED
----------------------------
- TestLibrary        — main component
- AddTagPopover      — popover shown when "+ Tag" button is clicked
- RemoveTagPopover   — popover shown when "- Tag" button is clicked
- MoveToSuiteModal   — modal for moving selected tests to a different suite
- TreeRow            — left-panel suite tree row

CALLER → CALLEE MAP
--------------------
- TestLibrary → AddTagPopover (via showAddTag state)
- TestLibrary → RemoveTagPopover (via showRemoveTag state)
- TestLibrary → MoveToSuiteModal (via showMoveModal state)
- TestLibrary → db.tests (CRUD), db.suites (CRUD)
- TestLibrary → onRunSelected prop → App.tsx execution flow

IMPORTANT POINTS
----------------
1. Tag filter chips (Filter by tag: row) use OR semantics: a test is shown if
   it has ANY of the selected tag chips. This matches the natural expectation
   of "show me all smoke tests OR sanity tests".
2. Run button priority: manual row checkbox selection > tag chip filter.
   When rows are checked, button says "Run Selected (N)"; when only tag filter
   is active (1 tag), it says "Run 'tagname' (N)".
3. Move Selected moves tests to a different SUITE (not a Run group).
   The modal pre-selects the current suite of the selected tests.
4. + Tag adds a tag to ALL selected tests (union — idempotent if already present).
5. - Tag lists the UNION of tags across all selected tests; removes from ALL.
6. Inline "add tag..." appears in the TAGS cell when a row is selected AND has
   no tags — lets the user add a tag without opening the bulk popover.
7. date filters compare against test.createdAt (creation date, not last run).

ISSUES FACED / KNOWN GOTCHAS
-----------------------------
- Popover close-on-outside-click uses a backdrop div overlay instead of
  document.addEventListener to avoid React synthetic event timing issues.
================================================================================
*/

import { useState, useMemo, useRef } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db, type DBTest, type DBSuite } from '../lib/db.js';
import { newId } from '@plex/ir';
import type { IRStep, TestIR } from '@plex/ir';

// ── Types ─────────────────────────────────────────────────────────────────────

type Props = {
  onLoad:        (steps: IRStep[], testId: string, testName: string) => void;
  onRun:         (steps: IRStep[], testId: string, testName: string) => void;
  onRunSelected: (tests: DBTest[]) => void;
};

type StatusFilter = 'all' | 'passed' | 'failed' | 'never_run';

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_DISPLAY: Record<string, { icon: string; label: string; color: string }> = {
  passed:        { icon: '✓', label: 'Passed',          color: '#22c55e' },
  failed:        { icon: '✗', label: 'Failed',          color: '#ef4444' },
  passed_healed: { icon: '✓', label: 'Passed (healed)', color: '#f59e0b' },
  error:         { icon: '⚠', label: 'Error',           color: '#f59e0b' },
};
const NEVER_RUN = { icon: '—', label: 'Never run', color: '#71717a' };

// ── AddTagPopover ─────────────────────────────────────────────────────────────

function AddTagPopover({
  selectedCount, allTags, onAdd, onClose,
}: {
  selectedCount: number;
  allTags: string[];
  onAdd: (tag: string) => void;
  onClose: () => void;
}) {
  const [input, setInput] = useState('');

  const submit = () => {
    const t = input.trim();
    if (t) { onAdd(t); setInput(''); }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
        onClick={onClose}
      />
      {/* Popover */}
      <div style={{
        position: 'absolute', top: '100%', left: 0, zIndex: 9999,
        background: '#1c1c1f', border: '1px solid #3f3f46', borderRadius: 8,
        padding: '14px 14px 10px', minWidth: 220,
        boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
        marginTop: 4,
      }}>
        <div style={{ fontSize: 12, color: '#71717a', marginBottom: 8 }}>
          Add tag to {selectedCount} test{selectedCount !== 1 ? 's' : ''}:
        </div>
        <input
          autoFocus
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') onClose(); }}
          placeholder="tag name…"
          style={{
            width: '100%', boxSizing: 'border-box',
            background: '#0f0f11', border: '1px solid #3f3f46', borderRadius: 5,
            padding: '6px 9px', color: '#e4e4e7', fontSize: 12, outline: 'none',
            marginBottom: 8,
          }}
        />
        {allTags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
            {allTags.map(tag => (
              <button
                key={tag}
                onClick={() => onAdd(tag)}
                style={{
                  padding: '2px 9px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                  background: '#1e1b4b', color: '#818cf8', border: 'none', cursor: 'pointer',
                }}
              >{tag}</button>
            ))}
          </div>
        )}
        <button
          onClick={submit}
          style={{
            width: '100%', background: '#7c3aed', border: 'none', borderRadius: 5,
            padding: '6px', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600,
          }}
        >Add</button>
      </div>
    </>
  );
}

// ── RemoveTagPopover ──────────────────────────────────────────────────────────

function RemoveTagPopover({
  selectedCount, tags, onRemove, onClose,
}: {
  selectedCount: number;
  tags: string[];
  onRemove: (tag: string) => void;
  onClose: () => void;
}) {
  return (
    <>
      <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }} onClick={onClose} />
      <div style={{
        position: 'absolute', top: '100%', left: 0, zIndex: 9999,
        background: '#1c1c1f', border: '1px solid #3f3f46', borderRadius: 8,
        padding: '14px 14px 10px', minWidth: 220,
        boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
        marginTop: 4,
      }}>
        <div style={{ fontSize: 12, color: '#71717a', marginBottom: 8 }}>
          Remove tag from {selectedCount} test{selectedCount !== 1 ? 's' : ''}:
        </div>
        {tags.length === 0 ? (
          <div style={{ fontSize: 12, color: '#52525b', padding: '4px 0' }}>No tags on selected tests</div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {tags.map(tag => (
              <button
                key={tag}
                onClick={() => { onRemove(tag); }}
                style={{
                  padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                  background: '#450a0a', color: '#f87171', border: '1px solid #7f1d1d',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                }}
              >✗ {tag}</button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// ── MoveToSuiteModal ──────────────────────────────────────────────────────────

function MoveToSuiteModal({
  selectedTests, suites, allTests, onMove, onCancel,
}: {
  selectedTests: DBTest[];
  suites: DBSuite[];
  allTests: DBTest[];
  onMove: (suiteId: string) => void;
  onCancel: () => void;
}) {
  const currentSuiteId = selectedTests.length === 1 ? selectedTests[0].suiteId : null;
  const [targetSuiteId, setTargetSuiteId] = useState(currentSuiteId ?? (suites[0]?.id ?? ''));

  const suiteCount = (id: string) => allTests.filter(t => t.suiteId === id).length;

  const movingLabel = selectedTests.length === 1
    ? `"${selectedTests[0].name}"`
    : `${selectedTests.length} tests`;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: '#1c1c1f', border: '1px solid #3f3f46', borderRadius: 10,
        padding: '24px 28px', minWidth: 380, maxWidth: 480,
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
      }}>
        <h3 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700, color: '#e4e4e7' }}>
          Move to suite
        </h3>
        <div style={{ fontSize: 12, color: '#71717a', marginBottom: 16, letterSpacing: '0.02em' }}>
          MOVING: {movingLabel.toUpperCase()}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 280, overflowY: 'auto', marginBottom: 20 }}>
          {suites.map(suite => {
            const isActive = targetSuiteId === suite.id;
            const isCurrent = currentSuiteId === suite.id;
            return (
              <div
                key={suite.id}
                onClick={() => setTargetSuiteId(suite.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 12px', borderRadius: 7, cursor: 'pointer',
                  background: isActive ? 'rgba(124,58,237,0.15)' : 'transparent',
                  border: isActive ? '1px solid rgba(124,58,237,0.4)' : '1px solid transparent',
                }}
              >
                <input
                  type="radio"
                  checked={isActive}
                  onChange={() => setTargetSuiteId(suite.id)}
                  style={{ accentColor: '#7c3aed', cursor: 'pointer' }}
                  onClick={e => e.stopPropagation()}
                />
                <span style={{ fontSize: 16 }}>📁</span>
                <span style={{ flex: 1, fontSize: 13, color: isActive ? '#c4b5fd' : '#a1a1aa', fontWeight: isActive ? 600 : 400 }}>
                  {suite.name}
                </span>
                <span style={{ fontSize: 12, color: '#52525b' }}>({suiteCount(suite.id)})</span>
                {isCurrent && (
                  <span style={{ fontSize: 10, color: '#52525b', fontStyle: 'italic' }}>current</span>
                )}
              </div>
            );
          })}
          {suites.length === 0 && (
            <div style={{ padding: 12, fontSize: 12, color: '#52525b', textAlign: 'center' }}>
              No suites available. Create a suite first.
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{
              background: 'none', border: '1px solid #3f3f46', borderRadius: 6,
              padding: '8px 20px', color: '#a1a1aa', cursor: 'pointer', fontSize: 13,
            }}
          >Cancel</button>
          <button
            disabled={!targetSuiteId || suites.length === 0}
            onClick={() => targetSuiteId && onMove(targetSuiteId)}
            style={{
              background: targetSuiteId ? '#7c3aed' : '#3f3f46',
              border: 'none', borderRadius: 6,
              padding: '8px 20px', color: targetSuiteId ? '#fff' : '#71717a',
              cursor: targetSuiteId ? 'pointer' : 'default', fontSize: 13, fontWeight: 600,
            }}
          >Move Here</button>
        </div>
      </div>
    </div>
  );
}

// ── TestLibrary ───────────────────────────────────────────────────────────────

export function TestLibrary({ onLoad, onRun, onRunSelected }: Props) {
  const suites   = useLiveQuery(() => db.suites.orderBy('createdAt').toArray(), []);
  const allTests = useLiveQuery(() => db.tests.orderBy('createdAt').toArray(), []);

  const [selectedSuiteId,   setSelectedSuiteId]   = useState<string | 'all'>('all');
  const [expanded,          setExpanded]           = useState<Set<string>>(new Set());
  const [search,            setSearch]             = useState('');
  const [statusFilter,      setStatusFilter]       = useState<StatusFilter>('all');
  const [dateFrom,          setDateFrom]           = useState('');
  const [dateTo,            setDateTo]             = useState('');
  const [activeTagFilters,  setActiveTagFilters]   = useState<Set<string>>(new Set());
  const [newSuiteName,      setNewSuiteName]       = useState('');
  const [showNewSuite,      setShowNewSuite]       = useState(false);
  const [confirmDelete,     setConfirmDelete]      = useState<string | null>(null);
  const [selected,          setSelected]           = useState<Set<string>>(new Set());
  const [showAddTag,        setShowAddTag]         = useState(false);
  const [showRemoveTag,     setShowRemoveTag]      = useState(false);
  const [showMoveModal,     setShowMoveModal]      = useState(false);
  const [inlineTagInput,    setInlineTagInput]     = useState<Record<string, string>>({});
  const importRef = useRef<HTMLInputElement>(null);

  // Hooks must come before any early return — use empty-array fallback until data loads
  const allTagsList: string[] = useMemo(() => {
    if (!allTests) return [];
    const set = new Set<string>();
    allTests.forEach(t => t.tags.forEach(tag => set.add(tag)));
    return [...set].sort();
  }, [allTests]);

  const tagsOnSelected: string[] = useMemo(() => {
    if (!allTests) return [];
    const set = new Set<string>();
    allTests.filter(t => selected.has(t.id)).forEach(t => t.tags.forEach(tag => set.add(tag)));
    return [...set].sort();
  }, [allTests, selected]);

  const visibleTests: DBTest[] = useMemo(() => {
    if (!allTests) return [];
    return allTests.filter(t => {
      if (selectedSuiteId !== 'all' && t.suiteId !== selectedSuiteId) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        if (!t.name.toLowerCase().includes(q) && !t.tags.some(tag => tag.toLowerCase().includes(q))) return false;
      }
      if (statusFilter === 'passed' && t.lastRunStatus !== 'passed' && t.lastRunStatus !== 'passed_healed') return false;
      if (statusFilter === 'failed' && t.lastRunStatus !== 'failed' && t.lastRunStatus !== 'error') return false;
      if (statusFilter === 'never_run' && t.lastRunStatus) return false;
      if (dateFrom && t.createdAt < new Date(dateFrom).getTime()) return false;
      if (dateTo && t.createdAt > new Date(dateTo).getTime() + 86_400_000) return false;
      if (activeTagFilters.size > 0 && !t.tags.some(tag => activeTagFilters.has(tag))) return false;
      return true;
    });
  }, [allTests, selectedSuiteId, search, statusFilter, dateFrom, dateTo, activeTagFilters]);

  if (!suites || !allTests) {
    return <div style={{ padding: 32, color: '#71717a', fontSize: 13, textAlign: 'center' }}>Loading…</div>;
  }

  const suiteTestCount = (suiteId: string) => allTests.filter(t => t.suiteId === suiteId).length;

  const toggleExpand = (id: string) => setExpanded(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const suiteName = (id: string) => suites.find(s => s.id === id)?.name ?? '—';

  // ── Tag chip filter toggle ───────────────────────────────────────────────

  const toggleTagFilter = (tag: string) => {
    setActiveTagFilters(prev => {
      const n = new Set(prev);
      n.has(tag) ? n.delete(tag) : n.add(tag);
      return n;
    });
  };

  // ── DB operations ────────────────────────────────────────────────────────

  const createSuite = async () => {
    const name = newSuiteName.trim();
    if (!name) return;
    const suite: DBSuite = { id: newId(), name, createdAt: Date.now(), updatedAt: Date.now() };
    await db.suites.add(suite);
    setNewSuiteName('');
    setShowNewSuite(false);
    setSelectedSuiteId(suite.id);
  };

  const deleteSuite = async (suiteId: string) => {
    const count = allTests.filter(t => t.suiteId === suiteId).length;
    if (count > 0) { alert(`Suite has ${count} test(s). Move or delete them first.`); return; }
    await db.suites.delete(suiteId);
    if (selectedSuiteId === suiteId) setSelectedSuiteId('all');
  };

  const deleteTest = async (testId: string) => {
    await db.tests.delete(testId);
    setConfirmDelete(null);
  };

  const bulkDelete = async () => {
    if (!confirm(`Delete ${selected.size} test(s)?`)) return;
    await db.tests.bulkDelete([...selected]);
    setSelected(new Set());
  };

  const addTagToTests = async (tag: string) => {
    const trimmed = tag.trim();
    if (!trimmed) return;
    const ids = [...selected];
    await Promise.all(ids.map(async id => {
      const test = await db.tests.get(id);
      if (!test || test.tags.includes(trimmed)) return;
      await db.tests.update(id, { tags: [...test.tags, trimmed], updatedAt: Date.now() });
    }));
    setShowAddTag(false);
  };

  const addTagToSingleTest = async (testId: string) => {
    const tag = (inlineTagInput[testId] ?? '').trim();
    if (!tag) return;
    const test = await db.tests.get(testId);
    if (!test || test.tags.includes(tag)) return;
    await db.tests.update(testId, { tags: [...test.tags, tag], updatedAt: Date.now() });
    setInlineTagInput(prev => ({ ...prev, [testId]: '' }));
  };

  const removeTagFromTests = async (tag: string) => {
    const ids = [...selected];
    await Promise.all(ids.map(async id => {
      const test = await db.tests.get(id);
      if (!test) return;
      await db.tests.update(id, { tags: test.tags.filter(t => t !== tag), updatedAt: Date.now() });
    }));
  };

  const moveToSuite = async (suiteId: string) => {
    const ids = [...selected];
    await Promise.all(ids.map(id => db.tests.update(id, { suiteId, updatedAt: Date.now() })));
    setShowMoveModal(false);
    setSelected(new Set());
  };

  const loadTest = (test: DBTest) => {
    try {
      const ir = JSON.parse(test.irJson) as TestIR;
      const steps = (ir.flows[0]?.steps ?? []) as IRStep[];
      onLoad(steps, test.id, test.name);
    } catch { /* corrupt IR */ }
  };

  const runTest = (test: DBTest) => {
    try {
      const ir = JSON.parse(test.irJson) as TestIR;
      const steps = (ir.flows[0]?.steps ?? []) as IRStep[];
      onRun(steps, test.id, test.name);
    } catch { /* corrupt IR */ }
  };

  const exportAll = () => {
    const blob = new Blob([JSON.stringify(allTests.map(t => JSON.parse(t.irJson)), null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'plex-tests.json'; a.click();
    URL.revokeObjectURL(url);
  };

  const exportSelected = () => {
    const tests = allTests.filter(t => selected.has(t.id));
    const blob = new Blob([JSON.stringify(tests.map(t => JSON.parse(t.irJson)), null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'plex-selected-tests.json'; a.click();
    URL.revokeObjectURL(url);
  };

  const importFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const ir = JSON.parse(text) as TestIR;
      let suiteId = suites[0]?.id;
      if (!suiteId) {
        const suite: DBSuite = { id: newId(), name: 'Imported', createdAt: Date.now(), updatedAt: Date.now() };
        await db.suites.add(suite);
        suiteId = suite.id;
      }
      await db.tests.add({
        id: ir.meta.id ?? newId(),
        suiteId,
        name: ir.meta.name ?? file.name,
        tags: ir.meta.tags ?? [],
        irJson: JSON.stringify(ir),
        stepCount: ir.flows[0]?.steps.length ?? 0,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      });
    } catch { alert('Invalid IR JSON file.'); }
    e.target.value = '';
  };

  const toggleSelect = (id: string) => setSelected(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const toggleSelectAll = () => {
    if (selected.size === visibleTests.length && visibleTests.length > 0) setSelected(new Set());
    else setSelected(new Set(visibleTests.map(t => t.id)));
  };

  // ── Run button label logic ───────────────────────────────────────────────

  const runBtnLabel = (): { label: string; testsToRun: DBTest[] } | null => {
    if (selected.size > 0) {
      return { label: `▶ Run Selected (${selected.size})`, testsToRun: allTests.filter(t => selected.has(t.id)) };
    }
    if (activeTagFilters.size === 1) {
      const tag = [...activeTagFilters][0];
      return { label: `▶ Run "${tag}" (${visibleTests.length})`, testsToRun: visibleTests };
    }
    if (activeTagFilters.size > 1) {
      return { label: `▶ Run filtered (${visibleTests.length})`, testsToRun: visibleTests };
    }
    return null;
  };

  const runBtn = runBtnLabel();

  // ── Selected tests for modals ────────────────────────────────────────────

  const selectedTests = allTests.filter(t => selected.has(t.id));

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: '#0f0f11' }}>

      {/* ── Header / Action bar ── */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #27272a', gap: 6, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#e4e4e7', marginRight: 4 }}>Test Cases</h2>

        {/* Run button — shows when rows selected OR tag filter active */}
        {runBtn && (
          <button
            onClick={() => onRunSelected(runBtn.testsToRun)}
            style={{ ...hdrBtn, background: '#7c3aed', color: '#fff', border: 'none', fontWeight: 700 }}
          >
            {runBtn.label}
          </button>
        )}

        {/* Move Selected */}
        {selected.size > 0 && (
          <button onClick={() => setShowMoveModal(true)} style={hdrBtn}>
            ↗ Move Selected ({selected.size})
          </button>
        )}

        {/* + Tag popover trigger */}
        {selected.size > 0 && (
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => { setShowAddTag(v => !v); setShowRemoveTag(false); }}
              style={{ ...hdrBtn, color: '#a1a1aa' }}
            >+ Tag</button>
            {showAddTag && (
              <AddTagPopover
                selectedCount={selected.size}
                allTags={allTagsList}
                onAdd={tag => void addTagToTests(tag)}
                onClose={() => setShowAddTag(false)}
              />
            )}
          </div>
        )}

        {/* - Tag popover trigger */}
        {selected.size > 0 && (
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => { setShowRemoveTag(v => !v); setShowAddTag(false); }}
              style={{ ...hdrBtn, color: '#a1a1aa' }}
            >- Tag</button>
            {showRemoveTag && (
              <RemoveTagPopover
                selectedCount={selected.size}
                tags={tagsOnSelected}
                onRemove={tag => void removeTagFromTests(tag)}
                onClose={() => setShowRemoveTag(false)}
              />
            )}
          </div>
        )}

        {/* Export Selected */}
        {selected.size > 0 && (
          <button onClick={exportSelected} style={hdrBtn}>
            ↓ Export Selected ({selected.size})
          </button>
        )}

        {/* Always-visible buttons */}
        <button onClick={exportAll} style={hdrBtn}>↓ Export All</button>
        <button onClick={() => importRef.current?.click()} style={hdrBtn}>↑ Import</button>
        <button onClick={() => setShowNewSuite(true)} style={{ ...hdrBtn, background: '#1c1c1f', color: '#e4e4e7' }}>+ New Suite</button>
        <input ref={importRef} type="file" accept=".json" style={{ display: 'none' }} onChange={e => void importFile(e)} />

        {/* Bulk delete (far right) */}
        {selected.size > 0 && (
          <button onClick={() => void bulkDelete()} style={{ ...hdrBtn, color: '#fca5a5', borderColor: '#7f1d1d', marginLeft: 'auto' }}>
            × Delete {selected.size}
          </button>
        )}
      </div>

      {/* ── Search + filters row ── */}
      <div style={{ padding: '8px 16px', borderBottom: '1px solid #1c1c1f', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or tag…"
          style={{
            flex: 1, minWidth: 160, background: '#18181b', border: '1px solid #3f3f46',
            borderRadius: 6, padding: '6px 10px', color: '#e4e4e7', fontSize: 12, outline: 'none',
          }}
        />
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value as StatusFilter)}
          style={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 6, padding: '6px 8px', color: '#a1a1aa', fontSize: 12 }}
        >
          <option value="all">All statuses</option>
          <option value="passed">Passed</option>
          <option value="failed">Failed</option>
          <option value="never_run">Never run</option>
        </select>

        {/* Date range */}
        <span style={{ fontSize: 11, color: '#52525b', flexShrink: 0 }}>Created after</span>
        <input
          type="date"
          value={dateFrom}
          onChange={e => setDateFrom(e.target.value)}
          style={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 6, padding: '5px 6px', color: '#a1a1aa', fontSize: 11, colorScheme: 'dark' }}
        />
        <span style={{ fontSize: 11, color: '#52525b', flexShrink: 0 }}>before</span>
        <input
          type="date"
          value={dateTo}
          onChange={e => setDateTo(e.target.value)}
          style={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 6, padding: '5px 6px', color: '#a1a1aa', fontSize: 11, colorScheme: 'dark' }}
        />

        {/* Clear date button */}
        {(dateFrom || dateTo) && (
          <button
            onClick={() => { setDateFrom(''); setDateTo(''); }}
            style={{ background: 'none', border: 'none', color: '#52525b', cursor: 'pointer', fontSize: 11, padding: '0 2px' }}
          >× Clear</button>
        )}

        {/* N tests count */}
        <span style={{ fontSize: 12, color: '#52525b', flexShrink: 0, marginLeft: 4 }}>
          {visibleTests.length} test{visibleTests.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* ── Tag filter chips row ── */}
      {allTagsList.length > 0 && (
        <div style={{
          padding: '6px 16px', borderBottom: '1px solid #1c1c1f',
          display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', flexShrink: 0,
        }}>
          <span style={{ fontSize: 11, color: '#52525b', flexShrink: 0 }}>Filter by tag:</span>
          {allTagsList.map(tag => {
            const isActive = activeTagFilters.has(tag);
            const count = allTests.filter(t => t.tags.includes(tag)).length;
            return (
              <button
                key={tag}
                onClick={() => toggleTagFilter(tag)}
                title={`${count} test${count !== 1 ? 's' : ''} with this tag`}
                style={{
                  padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                  background: isActive ? '#7c3aed' : '#1e1b4b',
                  color: isActive ? '#fff' : '#818cf8',
                  border: isActive ? 'none' : '1px solid #312e81',
                  cursor: 'pointer', transition: 'background 0.15s',
                }}
              >{tag}</button>
            );
          })}
          {activeTagFilters.size > 0 && (
            <button
              onClick={() => setActiveTagFilters(new Set())}
              style={{ background: 'none', border: 'none', color: '#52525b', cursor: 'pointer', fontSize: 11, padding: '0 2px' }}
            >× Clear</button>
          )}
        </div>
      )}

      {/* ── New suite inline form ── */}
      {showNewSuite && (
        <div style={{ padding: '8px 16px', borderBottom: '1px solid #27272a', display: 'flex', gap: 8, alignItems: 'center', background: '#111113', flexShrink: 0 }}>
          <input
            autoFocus
            value={newSuiteName}
            onChange={e => setNewSuiteName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') void createSuite(); if (e.key === 'Escape') setShowNewSuite(false); }}
            placeholder="New suite name…"
            style={{ flex: 1, background: '#09090b', border: '1px solid #7c3aed', borderRadius: 6, padding: '6px 10px', color: '#e4e4e7', fontSize: 13, outline: 'none' }}
          />
          <button onClick={() => void createSuite()} style={{ background: '#7c3aed', border: 'none', borderRadius: 5, padding: '6px 14px', color: '#fff', cursor: 'pointer', fontSize: 12 }}>Create</button>
          <button onClick={() => setShowNewSuite(false)} style={{ background: 'none', border: '1px solid #3f3f46', borderRadius: 5, padding: '6px 10px', color: '#71717a', cursor: 'pointer', fontSize: 12 }}>Cancel</button>
        </div>
      )}

      {/* ── Body: suite tree + test table ── */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Suite tree */}
        <div style={{ width: 210, flexShrink: 0, borderRight: '1px solid #27272a', overflowY: 'auto', paddingTop: 4 }}>
          <TreeRow
            label={`All Tests (${allTests.length})`}
            active={selectedSuiteId === 'all'}
            onClick={() => setSelectedSuiteId('all')}
            depth={0}
          />
          {suites.map(suite => {
            const count     = suiteTestCount(suite.id);
            const isExpanded = expanded.has(suite.id);
            const isActive  = selectedSuiteId === suite.id;
            return (
              <div key={suite.id}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <button
                    onClick={() => toggleExpand(suite.id)}
                    style={{ background: 'none', border: 'none', color: '#71717a', cursor: 'pointer', padding: '0 2px 0 8px', fontSize: 10, flexShrink: 0 }}
                  >{isExpanded ? '▼' : '▶'}</button>
                  <TreeRow
                    label={`${suite.name} (${count})`}
                    active={isActive}
                    onClick={() => setSelectedSuiteId(suite.id)}
                    depth={0}
                    isFolder
                    style={{ flex: 1 }}
                    onDelete={() => void deleteSuite(suite.id)}
                  />
                </div>
              </div>
            );
          })}
          {suites.length === 0 && (
            <div style={{ padding: '12px 16px', fontSize: 12, color: '#3f3f46' }}>No suites yet.</div>
          )}
        </div>

        {/* Test table */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {visibleTests.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: '#3f3f46', fontSize: 13 }}>
              {allTests.length === 0
                ? 'No saved tests yet.\nRecord a test and click "Save Test" to add it here.'
                : 'No tests match your filters.'}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #27272a', position: 'sticky', top: 0, background: '#0f0f11', zIndex: 1 }}>
                  <th style={th}>
                    <input
                      type="checkbox"
                      checked={selected.size === visibleTests.length && visibleTests.length > 0}
                      onChange={toggleSelectAll}
                      style={{ accentColor: '#7c3aed' }}
                    />
                  </th>
                  <th style={{ ...th, textAlign: 'left' }}>NAME</th>
                  <th style={{ ...th, textAlign: 'left' }}>SUITE</th>
                  <th style={{ ...th, textAlign: 'left' }}>LAST RUN</th>
                  <th style={{ ...th, textAlign: 'left' }}>STATUS</th>
                  <th style={{ ...th, textAlign: 'left' }}>TAGS</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {visibleTests.map(test => {
                  const stat       = test.lastRunStatus ? STATUS_DISPLAY[test.lastRunStatus] ?? NEVER_RUN : NEVER_RUN;
                  const isSelected = selected.has(test.id);
                  const lastRunDate = test.lastRunAt
                    ? new Date(test.lastRunAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : '—';

                  return (
                    <tr
                      key={test.id}
                      style={{
                        borderBottom: '1px solid #1c1c1f',
                        background: isSelected ? 'rgba(124,58,237,0.08)' : 'transparent',
                      }}
                    >
                      {/* Checkbox */}
                      <td style={td}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(test.id)}
                          style={{ accentColor: '#7c3aed' }}
                        />
                      </td>

                      {/* Name */}
                      <td style={{ ...td, fontWeight: 500 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {isSelected && <span style={{ color: '#52525b', fontSize: 14, cursor: 'grab' }}>⠿</span>}
                          <span style={{ color: '#71717a', fontSize: 12 }}>⚡</span>
                          <span style={{ color: '#e4e4e7', fontSize: 13 }}>{test.name}</span>
                        </div>
                      </td>

                      {/* Suite */}
                      <td style={{ ...td, color: '#a1a1aa', fontSize: 12 }}>{suiteName(test.suiteId)}</td>

                      {/* Last run */}
                      <td style={{ ...td, color: '#71717a', fontSize: 12 }}>{lastRunDate}</td>

                      {/* Status */}
                      <td style={td}>
                        <span style={{ color: stat.color, fontSize: 12, fontWeight: 500 }}>{stat.icon} {stat.label}</span>
                      </td>

                      {/* Tags */}
                      <td style={{ ...td, minWidth: 120 }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                          {test.tags.map(tag => (
                            <span
                              key={tag}
                              style={{
                                background: '#1e1b4b', color: '#818cf8', fontSize: 11,
                                borderRadius: 10, padding: '1px 8px', fontWeight: 600,
                              }}
                            >{tag}</span>
                          ))}
                          {/* Inline add tag for selected rows with no tags */}
                          {isSelected && test.tags.length === 0 && (
                            <input
                              value={inlineTagInput[test.id] ?? ''}
                              onChange={e => setInlineTagInput(prev => ({ ...prev, [test.id]: e.target.value }))}
                              onKeyDown={e => { if (e.key === 'Enter') void addTagToSingleTest(test.id); }}
                              placeholder="add tag…"
                              style={{
                                background: '#09090b', border: '1px solid #3f3f46', borderRadius: 5,
                                padding: '2px 7px', color: '#a1a1aa', fontSize: 11, outline: 'none',
                                width: 90,
                              }}
                            />
                          )}
                          {/* + tags link for unselected rows with no tags */}
                          {!isSelected && test.tags.length === 0 && (
                            <span
                              onClick={() => { toggleSelect(test.id); }}
                              style={{ color: '#3f3f46', fontSize: 12, cursor: 'pointer' }}
                            >+ tags</span>
                          )}
                        </div>
                      </td>

                      {/* Actions */}
                      <td style={{ ...td, textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', alignItems: 'center' }}>
                          <button onClick={() => loadTest(test)} style={rowBtn('#3f3f46', '#e4e4e7')}>Load</button>
                          <button onClick={() => runTest(test)} style={rowBtn('#7c3aed', '#fff')}>▶ Run</button>
                          {confirmDelete === test.id ? (
                            <>
                              <button onClick={() => void deleteTest(test.id)} style={rowBtn('#7f1d1d', '#fca5a5')}>Confirm</button>
                              <button onClick={() => setConfirmDelete(null)} style={rowBtn('#3f3f46', '#a1a1aa')}>Cancel</button>
                            </>
                          ) : (
                            <button onClick={() => setConfirmDelete(test.id)} style={rowBtn('transparent', '#52525b')}>×</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Move to Suite Modal ── */}
      {showMoveModal && (
        <MoveToSuiteModal
          selectedTests={selectedTests}
          suites={suites}
          allTests={allTests}
          onMove={suiteId => void moveToSuite(suiteId)}
          onCancel={() => setShowMoveModal(false)}
        />
      )}
    </div>
  );
}

// ── TreeRow ───────────────────────────────────────────────────────────────────

function TreeRow({ label, active, onClick, depth, isFolder, style: extraStyle, onDelete }: {
  label: string;
  active: boolean;
  onClick: () => void;
  depth: number;
  isFolder?: boolean;
  style?: React.CSSProperties;
  onDelete?: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <div
      style={{ display: 'flex', alignItems: 'center', paddingLeft: depth * 12 + (isFolder ? 0 : 16), ...extraStyle }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <button
        onClick={onClick}
        style={{
          flex: 1, textAlign: 'left', padding: '7px 10px',
          background: active ? 'rgba(124,58,237,0.2)' : hover ? '#1c1c1f' : 'none',
          border: 'none', color: active ? '#c4b5fd' : '#a1a1aa',
          cursor: 'pointer', fontSize: 12,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}
      >
        {isFolder ? '📁 ' : ''}{label}
      </button>
      {onDelete && hover && (
        <button
          onClick={e => { e.stopPropagation(); onDelete(); }}
          style={{ background: 'none', border: 'none', color: '#52525b', cursor: 'pointer', padding: '0 6px', fontSize: 13, flexShrink: 0 }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = '#ef4444'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = '#52525b'; }}
        >×</button>
      )}
    </div>
  );
}

// ── Style constants ───────────────────────────────────────────────────────────

const hdrBtn: React.CSSProperties = {
  background: 'none', border: '1px solid #3f3f46', borderRadius: 6,
  padding: '6px 11px', color: '#a1a1aa', cursor: 'pointer', fontSize: 12, fontWeight: 500,
  flexShrink: 0,
};

const th: React.CSSProperties = {
  padding: '8px 12px', fontSize: 11, fontWeight: 700,
  color: '#52525b', letterSpacing: '0.07em',
};

const td: React.CSSProperties = {
  padding: '9px 12px', verticalAlign: 'middle',
};

function rowBtn(bg: string, color: string): React.CSSProperties {
  return {
    background: bg, border: bg === 'transparent' ? '1px solid #3f3f46' : 'none',
    borderRadius: 5, padding: '4px 8px', color, cursor: 'pointer', fontSize: 11, fontWeight: 500,
  };
}
